/** <travel-club>: « Travel Club » blog page with a Mapbox map and a country list; each article carries a `custom.pays` metaobject exposed in data-* on this element, so the DOM is the only source of truth and marker, accordion and mobile tab stay in sync. Mapbox GL (~800 KB) is injected here rather than by theme.liquid. */
(function () {
  if (customElements.get("travel-club")) return;

  const BREAKPOINT = 750; // aligné sur styles/custom-travel-club.scss
  const MAPBOX_JS = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";

  // Plafond de zoom : la carte est un planisphère raster unique, pas des
  // tuiles — on reste volontairement dans une plage très serrée. Le plancher,
  // lui, est mesuré sur le conteneur (cf. #fitZoom).
  const ZOOM_MAX = { mobile: 2, desktop: 1.5 };
  // Plancher de repli, le temps que la carte existe : #fitZoom a besoin d'une carte vivante pour projeter.
  const ZOOM_FALLBACK = 0.05;
  // Mapbox n'accepte pas de minZoom en dessous de cette valeur.
  const ZOOM_FLOOR = -2;
  const ZOOM_ON_COUNTRY = 4;

  /** Emprise du planisphère, en degrés (l'image dépasse volontairement 180°). */
  const WORLD = { lng: 260, lat: 85 };

  let mapboxPromise = null;

  /** Charge Mapbox GL une seule fois, partagé entre instances. */
  function loadMapbox() {
    if (window.mapboxgl) return Promise.resolve();
    if (mapboxPromise) return mapboxPromise;

    mapboxPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = MAPBOX_JS;
      script.async = true;
      script.addEventListener("load", () => resolve());
      script.addEventListener("error", reject);
      document.head.appendChild(script);
    });
    return mapboxPromise;
  }

  class TravelClub extends HTMLElement {
    connectedCallback() {
      this.mapEl = this.querySelector("[data-travel-club-map]");
      this.drawer = this.querySelector("[data-travel-club-drawer]");
      this.accordions = Array.from(this.querySelectorAll(".travel_club_country"));
      this.tabs = Array.from(this.querySelectorAll(".travel_club_tab"));
      this.groups = Array.from(this.querySelectorAll(".travel_club_group"));

      this.markers = {};
      this.countries = {};
      /** Garde-fou : évite la boucle accordéon → carte → accordéon. */
      this.syncing = false;

      this.#readCountries();
      this.#bindAccordions();
      this.#bindTabs();
      this.#bindProgressBars();

      this.mq = window.matchMedia(`(max-width: ${BREAKPOINT - 1}px)`);
      if (this.isMobile) this.#syncMobileList();

      this.onMq = () => this.#handleBreakpointChange();
      this.mq.addEventListener("change", this.onMq);

      if (!this.mapEl || !this.dataset.mapboxToken) return;
      loadMapbox()
        .then(() => this.#initMap())
        .catch((err) => console.warn("[travel-club] chargement de Mapbox GL impossible", err));
    }

    disconnectedCallback() {
      this.mq?.removeEventListener("change", this.onMq);
      this.resizeObserver?.disconnect();
      this.map?.remove();
      this.map = null;
    }

    get isMobile() {
      return this.mq.matches;
    }

    /** Données pays lues sur les accordéons (data-* rendus par la section). */
    #readCountries() {
      this.accordions.forEach((el) => {
        const handle = el.dataset.country;
        if (!handle) return;

        const coordinates = (el.dataset.coordinates || "")
          .split(",")
          .map((value) => parseFloat(value.trim()));

        // Un pays sans coordonnées valides garde son accordéon mais n'a pas
        // de marqueur (le métaobjet peut être incomplet côté admin).
        if (coordinates.length !== 2 || coordinates.some(Number.isNaN)) {
          if (el.dataset.coordinates) {
            console.warn(`[travel-club] coordonnées invalides pour ${handle} :`, el.dataset.coordinates);
          }
          return;
        }

        this.countries[handle] = {
          name: el.dataset.countryName || handle,
          coordinates,
          image: el.dataset.markerImage || "",
          rotation: parseFloat(el.dataset.markerRotation) || 0,
        };
      });
    }

    // ---------------------------------------------------------------- Carte

    #maxZoom() {
      return this.isMobile ? ZOOM_MAX.mobile : ZOOM_MAX.desktop;
    }

    /** Zoom cadrant le planisphère sur le conteneur. Le raster couvre une emprise lng/lat fixe : le zoom qui l'affiche en entier dépend donc entièrement de la taille du conteneur, et une valeur en dur ne peut convenir qu'à une seule largeur — en mobile elle laissait la moitié du monde hors écran. */
    #fitZoom() {
      if (!this.map || !this.mapEl) return null;

      const { width, height } = this.mapEl.getBoundingClientRect();
      if (!width || !height) return null;

      const nw = this.map.project([-WORLD.lng, WORLD.lat]);
      const se = this.map.project([WORLD.lng, -WORLD.lat]);
      const imageWidth = Math.abs(se.x - nw.x);
      const imageHeight = Math.abs(se.y - nw.y);
      if (!imageWidth || !imageHeight) return null;

      // `max` = la carte remplit le conteneur en débordant sur le petit côté, jamais de bande vide. Un cran de zoom double l'échelle, d'où le log2.
      const scale = Math.max(width / imageWidth, height / imageHeight);
      return this.map.getZoom() + Math.log2(scale);
    }

    /** Recadre la carte sur son conteneur. Mapbox impose déjà ce cadrage via `maxBounds`, mais seulement en dessous de minZoom : laissé à une valeur fixe, celui-ci bloquait le mobile sur un zoom prévu pour la colonne desktop. */
    #frameMap({ animate = false } = {}) {
      if (!this.map) return;

      const fit = this.#fitZoom();
      if (fit === null) return;

      const min = Math.max(ZOOM_FLOOR, fit);
      this.map.setMaxZoom(Math.max(this.#maxZoom(), min));
      this.map.setMinZoom(min);

      if (animate) {
        this.map.flyTo({ center: [0, 0], zoom: min, duration: 1000 });
      } else {
        this.map.jumpTo({ center: [0, 0], zoom: min });
      }
    }

    #initMap() {
      window.mapboxgl.accessToken = this.dataset.mapboxToken;

      this.map = new window.mapboxgl.Map({
        container: this.mapEl,
        // Style entièrement local : aucune tuile Mapbox n'est appelée, juste
        // le planisphère fourni par le thème posé en source `image`.
        style: {
          version: 8,
          sources: {
            "map-background": {
              type: "image",
              url: this.dataset.mapTexture,
              coordinates: [
                [-WORLD.lng, WORLD.lat],
                [WORLD.lng, WORLD.lat],
                [WORLD.lng, -WORLD.lat],
                [-WORLD.lng, -WORLD.lat],
              ],
            },
          },
          layers: [
            { id: "background", type: "background", paint: { "background-color": "transparent" } },
            {
              id: "map-image",
              type: "raster",
              source: "map-background",
              paint: { "raster-opacity": 1 },
            },
          ],
        },
        center: [0, 0],
        zoom: ZOOM_FALLBACK,
        minZoom: ZOOM_FALLBACK,
        maxZoom: this.#maxZoom(),
        maxBounds: [
          [-WORLD.lng, -WORLD.lat],
          [WORLD.lng, WORLD.lat],
        ],
        projection: "mercator",
        renderWorldCopies: false,
        attributionControl: false,
        // Le scroll de la page ne doit pas zoomer la carte.
        scrollZoom: false,
      });

      this.map.addControl(
        new window.mapboxgl.NavigationControl({ showCompass: false, showZoom: true }),
        "top-right",
      );

      this.map.on("load", () => {
        this.#addMarkers();
        this.#frameMap();
      });

      // La carte est en aspect-ratio : recalculer la taille du canvas quand le
      // conteneur bouge (chargement des polices, rotation, resize…).
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.map?.resize());
        this.resizeObserver.observe(this.mapEl);
      }
    }

    #addMarkers() {
      Object.entries(this.countries).forEach(([handle, country]) => {
        const el = document.createElement("div");
        el.className = "travel_club_marker";
        el.setAttribute("role", "button");
        el.setAttribute("tabindex", "0");
        el.setAttribute("aria-label", country.name);

        if (country.image) {
          const img = document.createElement("img");
          img.className = "travel_club_marker_image";
          img.src = country.image;
          img.alt = "";
          img.loading = "lazy";
          img.style.setProperty("--marker-rotation", `${country.rotation}deg`);
          img.addEventListener("error", () => {
            console.warn(`[travel-club] image du marqueur illisible pour ${handle} :`, country.image);
            img.remove();
            el.appendChild(this.#markerFallback(country.name));
          });
          el.appendChild(img);
        } else {
          el.appendChild(this.#markerFallback(country.name));
        }

        const activate = () => this.#focusCountry(handle, { openPanel: true });
        el.addEventListener("click", activate);
        el.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          activate();
        });

        this.markers[handle] = new window.mapboxgl.Marker({ element: el, anchor: "center" })
          .setLngLat(country.coordinates)
          .addTo(this.map);
      });
    }

    #markerFallback(name) {
      const fallback = document.createElement("div");
      fallback.className = "travel_club_marker_fallback";
      fallback.textContent = name;
      return fallback;
    }

    /** Remet le marqueur du pays au premier plan (Mapbox n'ordonne pas les DOM markers). */
    #raiseMarker(handle) {
      const marker = this.markers[handle];
      if (!marker || !this.map) return;
      marker.remove();
      marker.addTo(this.map);
    }

    #flyTo(handle) {
      const country = this.countries[handle];
      if (!country || !this.map) return;
      this.map.flyTo({ center: country.coordinates, zoom: ZOOM_ON_COUNTRY, duration: 1000 });
      this.#raiseMarker(handle);
    }

    #resetMap() {
      this.#frameMap({ animate: true });
    }

    // --------------------------------------------------------- Synchronisation

    /** Single entry point: recenters the map and updates the country UI; `openPanel` is only true from a marker, the accordion and tab handling their own opening. */
    #focusCountry(handle, { openPanel = false } = {}) {
      this.#flyTo(handle);
      this.#setActiveTab(handle);

      if (this.isMobile) {
        this.#openDrawer(handle);
        return;
      }

      this.#closeDrawer();
      if (openPanel) this.#openAccordion(handle);
    }

    #bindAccordions() {
      this.accordions.forEach((el) => {
        const trigger = el.querySelector(".accordion__trigger");
        trigger?.addEventListener("click", () => {
          if (this.syncing) return;
          this.#flyTo(el.dataset.country);
          this.#setActiveTab(el.dataset.country);
        });
      });
    }

    /** Opens a country's accordion through a simulated click on its trigger, to reuse <accordion-component> rather than duplicating its logic. */
    #openAccordion(handle) {
      const el = this.accordions.find((item) => item.dataset.country === handle);
      if (!el || el.dataset.open === "true") return;

      this.syncing = true;
      el.querySelector(".accordion__trigger")?.click();
      this.syncing = false;

      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    #setActiveTab(handle) {
      this.tabs.forEach((tab) => {
        tab.classList.toggle("is-active", tab.dataset.country === handle);
        tab.setAttribute("aria-pressed", tab.dataset.country === handle ? "true" : "false");
      });
    }

    #bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const handle = tab.dataset.country;
          this.#setActiveTab(handle);

          // Onglet « Tous » : retour au planisphère, la liste repassant sur le groupe qui réunit tous les pays.
          if (handle === "all") {
            if (this.isMobile) {
              this.#openDrawer("all");
            } else {
              this.#closeDrawer();
            }
            this.#resetMap();
            return;
          }

          this.#openDrawer(handle);
          this.#flyTo(handle);
        });
      });
    }

    // -------------------------------------------------------------- Tiroir

    /** The mobile list is always on screen, so it always needs an active group: whichever tab is selected, « tous » on first render. */
    #syncMobileList() {
      const active = this.tabs.find((tab) => tab.classList.contains("is-active"));
      this.#openDrawer(active?.dataset.country || "all");
    }

    #openDrawer(handle) {
      const group = this.groups.find((item) => item.dataset.country === handle);
      if (!group || !this.drawer) return;

      this.groups.forEach((item) => item.classList.toggle("is-active", item === group));
      this.drawer.classList.add("is-open");
      this.drawer.setAttribute("aria-hidden", "false");
      this.#updateProgress(group);
    }

    #closeDrawer() {
      if (!this.drawer?.classList.contains("is-open")) return;
      this.drawer.classList.remove("is-open");
      this.drawer.setAttribute("aria-hidden", "true");
    }

    /** Barre de progression du strip d'articles, pilotée par le scroll. */
    #bindProgressBars() {
      this.groups.forEach((group) => {
        const scroller = group.querySelector(".travel_club_group_scroller");
        scroller?.addEventListener("scroll", () => this.#updateProgress(group), { passive: true });
      });
    }

    #updateProgress(group) {
      const scroller = group.querySelector(".travel_club_group_scroller");
      const bar = group.querySelector(".travel_club_progress_bar");
      if (!scroller || !bar) return;

      const scrollable = scroller.scrollWidth - scroller.clientWidth;
      const ratio = scrollable > 0 ? scroller.scrollLeft / scrollable : 1;
      bar.style.width = `${Math.min(Math.max(ratio, 0), 1) * 100}%`;
    }

    // ---------------------------------------------------------- Breakpoint

    /** Zoom levels differ between mobile and desktop and are only reapplied on breakpoint crossing: a raw `resize` recentered the map on every mobile URL bar move. */
    #handleBreakpointChange() {
      if (this.isMobile) {
        this.#syncMobileList();
      } else {
        this.#closeDrawer();
      }
      if (!this.map) return;

      // Le conteneur a changé de forme : le mesurer avant d'en déduire le cadrage.
      this.map.resize();
      this.#frameMap();
    }
  }

  customElements.define("travel-club", TravelClub);
})();
